// import { Relation } from "./relation"
import { Temporal } from '@js-temporal/polyfill';
import { CoordinatorService } from './coordinator.service';
import { TableInstance } from './table-instance';
import { Dataset } from './dataset';
import { Connection } from './connection';
import { Databin } from './databin';
import { ScriptItem } from './script-x-item';
import { BatchResult } from './batch-result';
import { BatchResultEmpty } from './batch-result-empty';
import { BatchResultItem } from './batch-result-item';
import { Batch } from './batch';

const whiteRegexp = /\s+/g;
const titleStartPageRegexp = /[^a-z0-9]/g;
const bracketRegexp = /\(.*?\)/g;
const sourceRegexp = /[\&,;:\-]+/g;

export type XITEM_TYPE = 'article' | 'patent' | 'web';

export class XItem {
    private _id?: string;
    kind: 'reference' | 'web' | 'search' = 'reference';
    private _keys?: Set<string>;
    private _tbli?: TableInstance;

    private _dataset: Dataset | undefined;
    ownerId: string | undefined;

    isDisplayDuplicates = false;

    // private _template: XitemTemplate
    templateValues = new Map<string, any>();

    duplicateLs: XItem[] = [];
    parentXitem: XItem | undefined;

    priority = 999;
    order = 0;
    payload: any;

    constructor(
        public coords: CoordinatorService,
        params?: any,
    ) {
        this.ownerId = params && params.ownerId;
        if (this.ownerId) delete params.ownerId;
        params && this.importData(params);
    }

    get values() {
        return this.coords.valueItem$.xitemValueConnector(this.id);
    }

    get conditions() {
        return this.coords.condition$.xitemConditionConnector(this.id);
    }

    get subsets() {
        return this.coords.view$.xitemSubsetConnector(this.id);
    }

    get ratings() {
        return this.coords.binaryTree$.xitemRatingConnector(this.id);
    }

    get tags() {
        return this.coords.binaryTree$.xitemTagConnector(this.id);
    }

    setType(tp: XITEM_TYPE) {
        ['isWeb', 'isPatent'].map((k) => this.templateValues.delete(k));
        switch (tp) {
            case 'patent':
                this.templateValues.set('isPatent', true);
                break;
            case 'web':
                this.templateValues.set('isWeb', true);
                break;
        }
        this._tbli = undefined;
    }

    copy() {
        const xitm = new XItem(this.coords, this.save());
        xitm.id = this.id;
        xitm.ownerId = this.ownerId;
        return xitm;
    }

    assignDataset(dt: Dataset) {
        this._dataset = dt;
    }

    get dataset() {
        return this._dataset;
    }

    get id() {
        return this._id!;
    }

    get csl() {
        return {
            id: this.id,
            type: 'article-journal',
            title: this.title,
            author: this.get('authorLs')?.map((x: string) => ({ family: x })),
            volume: this.get('volume'),
            issue: this.get('issue'),
            'container-title': this.get('sourceAbr') || this.get('source'),
            DOI: this.get('doi'),
            URL: this.get('url'),
            page: this.get('startPage'),
            issued: {
                'date-parts': [[this.get('year')]],
            },
        };
    }

    manifestStore() {
        this.coords.dataset$.main.markForManifestStore();
    }

    markForManifestStore() {
        this.manifestStore();
    }

    dialog() {
        this.coords.display$.dialog$.next({
            action: 'xitem',
            xitm: this,
        });
    }

    cite(params?: any) {
        if (!params) params = { inDetail: true };
        return this.scriptClass.cite(params);
    }

    docx(params?: any) {
        return this.coords.citation$.docx(this, params);
    }

    switchShowDuplicates(event: Event) {
        event.stopPropagation();
        this.isDisplayDuplicates = !this.isDisplayDuplicates;
        this.coords.display$.markForCheck();
        // this.coords.dataset$.refreshXItemDisplay$.next(this)
    }

    isNoDuplicateOf(ofXItm: XItem) {
        const d$ = this.coords.dataset$;
        d$.addNoDuplicate(ofXItm.id, this.id);
        d$.main?.requestReduplicate();
        d$.preview?.requestReduplicate();
    }

    get allIdLs() {
        const ls: string[] = [];
        ls.push(this.id);
        this.duplicateLs.forEach((x) => ls.push(x.id));
        return ls;
    }

    get databinMap() {
        const mp = new Map<Databin, Connection[]>();
        const idLs = this.allIdLs;
        this.coords.databin$.activeConnectionLs.forEach((con) => {
            for (const xitmId of idLs) {
                if (con.hasId(xitmId)) {
                    let cLs = mp.get(con.databin!);
                    if (!cLs) {
                        cLs = [];
                        mp.set(con.databin!, cLs);
                    }
                    cLs.push(con);
                    break;
                }
            }
        });
        return mp;
    }

    get noDuplicateSet() {
        return this.coords.dataset$.noDuplicateSet(this.id);
    }

    idTest(tid: string) {
        const doi = this.get('doi');
        if (this.id == tid || doi == tid || this.get('url') == tid) return true;

        for (let xitm of this.duplicateLs)
            if (xitm.id == tid || doi == tid || xitm.get('url') == tid) return true;
        return false;
    }

    get context() {
        return {
            _type: 'reference',
            title: this.title,
            abstract: this.abstract,
            source: this.get('source'),
            doi: this.get('doi'),
            url: this.get('url'),
            author: this.get('author'),
            publicationDate: this.get('publicationDate'),
            retrievedDate: this.get('retrievedDate'),
            lastVisitedDate: this.get('lastVisitedDate'),
        };
    }

    get idList() {
        const ls = [this.id];
        this.duplicateLs.forEach((xitm) => ls.push(xitm.id));
        return ls;
    }

    spaceRx = /\s+/g;

    set id(txt: string) {
        const minLen = 8;
        if (txt) {
            txt = txt.replace(this.spaceRx, '');
            const i = txt.indexOf('(', minLen);
            if (i !== -1) txt = txt.substring(0, i);
            const j = txt.indexOf(';', minLen);
            if (j !== -1) txt = txt.substring(0, j);

            this._id = txt;
        }
    }

    generateId() {
        const doi = this.get('doi');
        if (doi) this._id = doi;
        this._id = this.coords.uniqueId('gen');
    }

    get keys() {
        if (!this._keys) {
            this._uniqueKeys();
        }
        return this._keys!;
    }

    get keysIncludingDuplicates() {
        const s = new Set(this.keys);
        this.duplicateLs.forEach((dxitm) => dxitm.keys!.forEach((k) => s.add(k)));
        return s;
    }

    sharesKey(xitm: XItem) {
        for (let k of this.keys!) if (xitm.keys!.has(k)) return true;
        return false;
    }

    get type() {
        if (this.isPatent) return 'patent';
        const t = this.get('importedType');
        if (t) return t;
        return 'article';
    }

    // suggestCrossrefCitation(params?: {
    //     doi?: string,
    //     title?: string, fun?: () => void
    // }) {

    // }

    select(tag: string) {
        return this.templateValues.get(tag);
    }

    tbli(): TableInstance {
        let t = this.type;
        if (!['web', 'article', 'patent'].includes(t)) t = 'article';

        if (!this._tbli) {
            this._tbli = this.coords.table$.createTableInstance({
                origin: this,
                templateId: `biblio-${t}`,
                forceNew: true,
            });
        }
        return this._tbli;
    }

    get datasetWorkerData() {
        const dct: any = {
            id: this.id,
            type: this.type,
            abstract: this.abstract,
        };

        ['title', 'year', 'source', 'langLs', 'authorLs', 'doi', 'cpcLs', 'ipcLs'].forEach(
            (x) => (dct[x] = this.get(x)),
        );

        return dct;
    }

    get overlapData() {
        return {
            id: this.id,
            type: this.type,
            duplIdLs: this.duplicateLs.map((x) => x.id),
        };
    }

    exists(tag: string) {
        return this.templateValues.get(tag) !== undefined;
    }

    get isSubmitReady() {
        const tbli = this.tbli();
        const tC = tbli.getCellValue('title');
        if (tC && !tC.editText.trim()) return false;
        if (this.isWeb) {
            const tU = tbli.getCellValue('url');
            if (tU && !tU.editText.trim()) return false;
        }
        return true;
    }

    private _hit_year(value: string, test: string) {
        const match = test.match(/^([^\d]*)(\d+)$/);
        if (!match) return false;
        const operator = match[1] || '=';
        const testN = parseInt(match[2], 10);

        const year = parseInt(value, 10);
        if (isNaN(year)) return false;

        switch (operator) {
            case '==':
            case '=':
                return year == testN;
            case '<':
                return year < testN;
            case '>':
                return year > testN;
            case '>=':
                return year >= testN;
            case '<=':
                return year <= testN;
        }
        return false;
    }

    hit(tag: string, test: string | number | RegExp | Function): boolean {
        if (typeof test == 'number') test = `${test}`;

        if (tag === 'text') {
            return this.hit('title', test) || this.hit('abstract', test);
        }

        let v = this.get(tag);
        if (!v) return false;

        if (tag === 'year') return this._hit_year(v, test as string);

        if (Array.isArray(v)) {
            if (tag.endsWith('-first')) v = [v[0]];
            else if (tag.endsWith('-last')) v = [v[v.length - 1]];

            for (const vItem of v) {
                let r: boolean;
                if (typeof test == 'string') {
                    try {
                        r = vItem.toLowerCase().includes(test.toLowerCase());
                    } catch (e) {
                        return false;
                    }
                } else if (typeof test == 'function') r = test(v);
                else {
                    test.lastIndex = 0;
                    r = test.test(vItem);
                }
                if (r) return true;
            }
            return false;
        } else {
            if (typeof test == 'string') return v && v.toLowerCase().includes(test.toLowerCase());
            else if (typeof test == 'function') return test(v);
            test.lastIndex = 0;
            return test.test(v);
        }
    }

    private _importedPublicationTypeLs() {
        if (this.isPatent) return ['patent'];
        if (this.isWeb) return ['webpage'];
        let val =
            this.templateValues.get('documentType') || this.templateValues.get('publicationType');

        let ls: string[] = [];
        if (val) {
            ls = (val as string)
                .split(';')
                .map((x) =>
                    this.coords.utils$.normalizeSpace(x, { toLower: true, spaceReplacement: '_' }),
                )
                .map((x) => this.coords.config$.importedDocumentTypeMap.get(x) || x);
        }
        if (!ls || !ls.length) {
            ls = [];
            const isbnLs = this.templateValues.get('isbnLs');
            if (isbnLs && isbnLs.length) ls.push('book');
        }

        if (ls.length > 1) {
            if (['article', 'review', 'editorial_material'].includes(ls[0])) {
                const x = ls.shift()!;
                ls.push(x);
            }
        }

        return ls;
    }

    get(tag: string) {
        if (!tag) return '';
        if (tag == 'id') return this._id;
        if (tag == 'importedType') return this._importedPublicationTypeLs();

        // if (v && !['publicationDate', 'publicationType'].includes(tag)) return v;
        switch (tag) {
            case 'publicationType':
                return this._importedPublicationTypeLs();
            case 'doi':
                const doi = this.templateValues.get('doi')?.trim().split(' - ')[0] || '';
                if (!doi.startsWith('10.')) return '';
                return doi;
            case 'title':
                return this.templateValues.get('title') || this.templateValues.get('source') || '';
            case 'publicationDate':
            case 'retrievedDate':
            case 'lastVisitedDate':
                const dStr = this.templateValues.get(tag);
                if (!dStr) return '';
                try {
                    return Temporal.PlainDate.from(dStr);
                } catch (e) {
                    return '';
                }
            case 'year':
                const y = this.templateValues.get('year');
                if (y) return y.toString();
                const pD: any = this.get('publicationDate');
                if (pD?.year) return pD.year.toString();
                return '';

            case 'language':
                return this.templateValues.get('langLs');
            case 'authorLs':
            case 'author':
            case 'author-first':
            case 'author-last':
                return this.isPatent
                    ? this.templateValues.get('inventorLs')
                    : this.templateValues.get('authorLs');
            case 'inventorLs':
                return this.isPatent
                    ? this.templateValues.get('authorLs')
                    : this.templateValues.get('inventorLs');
            case 'source':
                if (this.isPatent) {
                    const pn = this.templateValues.get('patentNumber') || '';
                    return `${pn.slice(0, 2)} patent `.trim();
                } else {
                    return this.templateValues.get('source');
                }
            case 'firstPage':
            case 'startPage':
                return this.templateValues.get('firstPage') || this.templateValues.get('startPage');
            case 'lastPage':
            case 'endPage':
                return this.templateValues.get('lastPage') || this.templateValues.get('endPage');

            default:
                return this.templateValues.get(tag);
        }
    }

    normRegexp = /\s+/g;

    get scriptClass() {
        return new ScriptItem(this.coords, this);
    }

    manualValueChange(tag: string, value: any) {
        console.log('manualValueChange', tag, value);
    }

    set(tag: string, val: any) {
        const isArray = Array.isArray(val);
        if (tag === 'year') val = val.toString();

        const oldVal = this.templateValues.get(tag);
        if (isArray && typeof val[0] == 'string') val = val.map((x: string) => x.trim());

        try {
            if (
                val === oldVal ||
                (val === false && oldVal === undefined) ||
                JSON.stringify(val) === JSON.stringify(oldVal)
            )
                return;
        } catch (e) {}

        if ((!isArray && val == '') || val === undefined) {
            this.templateValues.delete(tag);
        } else {
            if (typeof val == 'string') {
                val = val.replace(this.normRegexp, ' ').trim();
                if (oldVal === val) return;
            }
            this.templateValues.set(tag, val);
        }
        this._dataset?.requestXitemUpdate(this);
    }

    get title(): string {
        return this.get('title');
    }

    get shortID() {
        const n = this.shortIDdecimal;
        if (!n) return;
        return n.toString(36);
    }

    get shortIDdecimal() {
        return this.templateValues.get('_shortID');
    }

    set shortIDdecimal(val: number) {
        this.templateValues.set('_shortID', val);
    }

    get abstract(): string {
        const abstr = this.get('abstract');
        if (abstr) return abstr;
        for (let xitm of this.duplicateLs) if (xitm.abstract) return xitm.abstract;
        return '';
    }

    clone(id?: string) {
        const xitm = new XItem(this.coords);
        // xitm._template = this._template
        xitm.templateValues = new Map<string, any>(this.templateValues);

        xitm.id = id || this.id;
        return xitm;
    }

    get isWeb() {
        if (this.get('isWeb')) return true;
        return this.get('url') && this.get('retrievedDate');
    }

    get patentMainDate() {
        const [type, value] = [null, null];

        if (!this.isPatent) return { type, value };
        for (const type of ['priorityDate', 'filingDate', 'publicationDate', 'grantDate']) {
            const value = this.get(type);
            if (value) return { type, value };
        }
        return { type, value };
    }

    get isPatent(): boolean {
        if (this.get('isPatent')) return true;
        return !!this.get('patentNumber');
    }

    get biblioTemplate() {
        const t$ = this.coords.template$;
        if (this.isPatent) return t$.get('biblio-patent');
        if (this.isWeb) return t$.get('biblio-web');
        return t$.get('biblio-article');
    }

    get biblioTableTagLs() {
        let t: string;
        if (this.isPatent) t = 'patent';
        else if (this.isWeb) t = 'web';
        else t = 'article';

        return (this.coords.config$.biblioTable(t)?.tagLs || []).map((x) => {
            const tag = x.tag;
            const value = this.get(tag);
            let displayValue = '';

            if (value && tag === 'url')
                displayValue = `<a target="_blank" href="${value}">${value}</a>`;
            else if (value && tag === 'doi')
                displayValue = `<a target="_blank" href="https://dx.doi.org/${value}">${value}</a>`;
            else if (tag === 'id' && !this.dataset?.isPreview) {
                displayValue = this.shortID!;
            }
            const hideInView = ['title', 'abstract'].includes(tag);
            const inViewMode =
                value && (value.length === undefined || value.length > 0) && !hideInView;

            const pin = `biblio-table-${tag}`;
            return { tag, type: x.type, value, displayValue, hideInView, inViewMode, pin };
        });
    }

    refreshKeys() {
        this._uniqueKeys();
    }

    private _uniqueKeys() {
        this._keys ? this._keys.clear() : (this._keys = new Set<string>());
        const d$ = this.coords.dataset$;

        const idLs = ['id', 'doi', 'url'];
        idLs.forEach((x) => {
            const tag = `${x.split('-')[0]}-lowercase`;
            if (d$.isOnDuplkey(tag)) {
                const v = this.get(x);
                try {
                    v && this._keys!.add(v.toLowerCase());
                } catch (e) {}
            }
        });

        const t = this.title?.toLowerCase().replace(titleStartPageRegexp, '') || '';

        const v = this.get('volume');
        const y = this.get('year');
        const sp = this.get('startPage');
        let source: string;
        try {
            source = this.get('source')?.toLowerCase() || '';
        } catch (e) {
            source = '';
        }

        if (d$.isOnDuplkey('source-volume-year-startPage') && source && v && y && sp)
            this._keys.add(`SVY_${t.slice(0, 8)}|${source}|${y}|${v}|${sp}`);

        if (d$.isOnDuplkey('title-startPage') && sp && t.length > 14)
            this._keys.add(`TSP_${t.slice(0, 100)}|${sp}`);

        const authorLs = this.get('authorLs') || [];
        if (d$.isOnDuplkey('authors-title-startPage-year') && y && t && sp && authorLs.length) {
            const authStr = authorLs
                .slice(0, 3)
                .map((auth: string) => {
                    try {
                        return auth.split(',')[0].trim().toLowerCase().replace(/\W+/g, 'Q');
                    } catch (e) {
                        return '';
                    }
                })
                .join('&');
            this._keys.add(`AUT_${authStr}|${t}|${sp}|${y}`);
        }
    }

    importData(params: any) {
        this.id = params.id;
        this.priority = 5;

        params.authorLs = params.authorLs || params.author;

        Object.keys(params).forEach((k) => {
            let v = params[k];
            if (k.endsWith('Date')) {
                try {
                    v = Temporal.Instant.from(v);
                } catch (e) {}
            }
            if (k == 'year' && Array.isArray(v)) {
                v = v[0];
                if (typeof v == 'string') v = parseInt(v).toString() || undefined;
            }
            if (v && typeof v == 'string') {
                v = this.coords.utils$.normalizeSpace(v, {
                    hyphen: k === 'title' || k === 'abstract',
                });
            }
            this.templateValues.set(k, v);
        });
        this.keys;
    }

    exportEcomole(params?: any) {
        params = params || {};
        const dct = {
            id: this.id,
            title: this.title,
            abstract: this.abstract,
            doi: this.get('doi'),
            author: this.get('authorLs'),
            type: this.type,
            url: this.get('url'),
            year: this.get('year'),
            volume: this.get('volume'),
            startPage: this.get('startPage'),
            source: this.get('source'),
            lang: this.get('lang'),
        };
        return dct;
    }

    async preview() {
        this.coords.dataset$.isSingleItemMode = true;
        await this.coords.utils$.wait(1);
        this.coords.resultList$.activeItem = this;
        this.coords.display$.markForCheck();
    }

    get sortedViewLs() {
        return this.coords.view$.sortedViewLs.filter((v) => v.has(this.id));
    }

    highlight(tp: string) {
        const txt = this.get(tp);
        if (!txt) return '';
        return this.coords.highlight$.run(txt, tp);
        // return this.coords.highlight$.run(, tp)
    }

    get keywordLs() {
        return this.coords.keyword$.xitemKeywordLs(this);
    }

    get keywordSum() {
        let s = 0;
        this.keywordLs.forEach((k) => (s += k.weight));
        return s;
    }

    hasKeyword(txt: string) {
        for (let kw of this.keywordLs) {
            if (kw.source?.toLowerCase() == txt.toLowerCase()) return true;
        }
        return false;
    }

    private _removeMarkup(txt: string) {
        if (!txt) return txt;

        return txt.replace(/<.*?>/g, ' ').trim();
    }

    save() {
        return this.coords.map2json(this.templateValues, { id: this.id });
    }

    exportJsonAll() {
        const dct = this.exportMemowl();

        dct['shortId'] = this.shortID;
        delete dct['_shortID'];

        dct['citation'] = this.cite({ isShort: true, isPlainText: true });

        dct['subsets'] = this.inSubsetLs;
        dct[this._dataset?.isPreview ? 'collectionId' : 'previewId'] = this.otherDatasetId;
        dct['ratings'] = this.coords.binaryTree$.exportJsonAllXitem('ratings', this.id);
        dct['tags'] = this.coords.binaryTree$.exportJsonAllXitem('tags', this.id);
        dct['comments'] = this.coords.comment$.exportJsonAllXitem(this.id);
        return dct;
    }

    get otherDatasetId() {
        if (this._dataset?.isPreview) return this.coords.dataset$.fromPreview2MainId(this.id);
        else return this.coords.dataset$.fromMain2PreviewId(this.id);
    }

    get otherDatasetXitem(): XItem | undefined {
        const otherId = this.otherDatasetId;
        if (!otherId) return undefined;
        const d$ = this.coords.dataset$;
        const dtOther = this._dataset?.isPreview ? d$.main : d$.preview;
        return dtOther.get(otherId);
    }

    get inSubsetLs() {
        const ls: string[] = [];
        this.coords.view$.viewLs.forEach((v) => {
            if (v.has(this.id)) ls.push(v.id);
        });
        return ls;
    }

    exportMemowl(params?: any) {
        params = params || {};

        const dct = Object.fromEntries(this.templateValues);
        Object.keys(dct).forEach((k) => {
            const v = dct[k];
            if (v && v.toJSON) dct[k] = v.toJSON();
        });

        if (params.removeMarkup) {
            dct['title'] = this._removeMarkup(dct['title']);
            if (dct['abstract']) dct['abstract'] = this._removeMarkup(dct['abstract']);
        }

        if (params.renameFields) {
            Object.keys(params.renameFields).forEach((k) => {
                dct[params.renameFields[k]] = dct[k];
                delete dct[k];
            });
        }
        dct['id'] = this.id;
        dct['type'] = this.type;
        return dct;
    }

    get hasDuplicate() {
        return !!this.duplicateLs.length;
    }

    get hasAttachment() {
        return !!this.coords.attachment$.attachmentLs(this.id).length;
    }

    get attachmentLs() {
        return this.coords.attachment$.attachmentLs(this.id);
    }

    get hasComment() {
        const mp = this.coords.comment$.getItemMap(this.id);
        if (!mp) return false;
        for (const c of mp.values()) {
            if (!c.isEmpty) return true;
        }
        return false;
    }

    hasValue(field: string, value: string): boolean {
        const v = this.get(field);
        if (Array.isArray(v)) return v.includes(value);
        else return v === value;
    }

    async batch(tp: string) {
        const params: any = {
            type: tp,
            cache: 'use',
            itemLs: [this.exportMemowl()],
            respTarget: {
                type: 'item',
                id: this.id,
            },
        };

        const b = new Batch(this.coords, { payload: params });
        b.start();
    }

    hasBatch(br: BatchResult, tag: string) {
        // const br = this.coords.batch$.getResult(tag);
        if (!br) return false;
        const brItm = br.itmData(this.id);
        if (!brItm) return false;
        switch (tag) {
            case 'no-hit':
                return brItm instanceof BatchResultEmpty;
            case 'change':
                return br.changeSet.has(this.id);
            case 'ok':
                return brItm instanceof BatchResultItem && brItm.isOK;
            case 'suggest':
                return brItm instanceof BatchResultItem && brItm.hasSuggest;
            default:
                return true;
        }
    }

    private _risRow(tag: string, value: string, ls: string[]) {
        if (!value && tag != 'ER') {
            return;
        }
        ls.push(`${tag}  - ${value}`);
    }

    exportRIS() {
        const tmp: any[] = [];
        this._risRow('TY', this.type, tmp);
        this._risRow('TI', this.title, tmp);

        const fLs = [
            ['VL', 'volume'],
            ['IS', 'issue'],
            ['SP', 'startPage'],
            ['AN', 'articleNumber'],
            ['PY', 'year'],
            ['DO', 'doi'],
            ['AU', 'authorLs'],
            ['SN', 'issn'],
            ['LA', 'langLs'],
            ['UR', 'url'],
            ['SE', 'bookSeries'],
            ['CY', 'confLocation'],
            ['CT', 'confTitle'],
            ['DB', 'dbName'],
            ['LC', 'earliestPriority'],
            ['BE', 'editorLs'],
            ['EI', 'eissnLs'],
            ['EP', 'endPage'],
            ['BN', 'isbnLs'],
            ['SN', 'issnLs'],
            ['LB', 'late_priority'],
            ['OI', 'orcid'],
            ['PG', 'pageCount'],
            ['PN', 'patentNumber'],
            ['PB', 'publisher'],
            ['PI', 'publisherCity'],
            ['T2', 'source'],
            ['J2', 'sourceAbr'],
            ['PY', 'confYear'],
            ['M3', 'documentType'],
            ['MA', 'meeting_ab'],
        ];

        fLs.forEach((tpl) => {
            let v = this.get(tpl[1]);
            if (!v) return;
            if (!Array.isArray(v)) v = [v];
            v.forEach((x: any) => this._risRow(tpl[0], x, tmp));
        });

        this._risRow('AB', this.abstract, tmp);
        this._risRow('ID', this.id, tmp);
        this._risRow('ER', '', tmp);
        return tmp.join('\n');
    }

    hasKey(k: string) {
        return this.id == k || this.keys!.has(k);
    }

    hasIncludingDuplicatesKey(k: string) {
        if (this.hasKey(k)) return true;
        for (let d of this.duplicateLs) {
            if (d.hasKey(k)) return true;
        }
        return false;
    }

    exportIdentifiers(isOther = false): string[] {
        const s = new Set<string>();
        s.add(this.id);
        const doi = this.get('doi');
        doi && s.add(doi);
        const url = this.get('url');
        url && s.add(url);

        this.duplicateLs.forEach((dXitem) => {
            dXitem.exportIdentifiers(true).forEach((id) => s.add(id));
        });

        if (!isOther) {
            const otherXitem = this.otherDatasetXitem;
            otherXitem?.exportIdentifiers(true).forEach((id) => s.add(id));
        }
        return Array.from(s);
    }
}
